import type { CodebaseProfile, TokenFormat } from './github-types';

export interface DerivedTokenConfigSource {
  filePath: string;
  format: string;
  isPrimary: boolean;
}

export interface DerivedTokenConfig {
  sources: DerivedTokenConfigSource[];
  prefix?: string;
  stylingApproach: string;
  thirdPartyDS?: string;
}

export interface TokenConfigConfidence {
  level: 'high' | 'low';
  reasons: string[];
}

export interface DerivedTokenConfigResult {
  tokenConfig: DerivedTokenConfig;
  confidence: TokenConfigConfidence;
}

const MANUAL_SETUP_FORMATS: TokenFormat[] = [
  'tailwind-v3-config',
  'js-theme-object',
];

const MIN_TOKENS_FOR_PREFIX_EXPECTATION = 5;

export function deriveTokenConfig(
  profile: CodebaseProfile,
): DerivedTokenConfigResult {
  const sources: DerivedTokenConfigSource[] = profile.tokens.sources.map(
    (src, i) => ({
      filePath: src.filePath,
      format: src.format,
      isPrimary: i === 0,
    }),
  );

  const tokenConfig: DerivedTokenConfig = {
    sources,
    stylingApproach: profile.styling.primary,
    ...(profile.tokens.primaryPrefix
      ? { prefix: profile.tokens.primaryPrefix }
      : {}),
    ...(profile.thirdPartyDS?.name
      ? { thirdPartyDS: profile.thirdPartyDS.name }
      : {}),
  };

  const reasons: string[] = [];

  if (profile.tokens.format === 'mixed') {
    reasons.push(
      'Multiple token formats detected — we picked the dominant one as primary.',
    );
  }

  if (MANUAL_SETUP_FORMATS.includes(profile.tokens.format)) {
    reasons.push(
      'Token format needs manual configuration — automatic detection only covers a subset.',
    );
  }

  if (
    profile.tokens.totalCount >= MIN_TOKENS_FOR_PREFIX_EXPECTATION &&
    !profile.tokens.primaryPrefix
  ) {
    reasons.push(
      'No common token prefix detected — governance may not distinguish your tokens from third-party CSS.',
    );
  }

  if (profile.styling.primary === 'unknown') {
    reasons.push(
      "Styling approach couldn't be determined — context guidance will be generic.",
    );
  }

  return {
    tokenConfig,
    confidence: {
      level: reasons.length === 0 ? 'high' : 'low',
      reasons,
    },
  };
}
